<template>
  <div class="row">
    <div class="medium-6 columns">
      <section>
        <h1 v-html="stepHeadline" />

        <!-- Hvornår opdagede du tyveriet -->
        <article class="flow-group">
          <h3 v-html="whenHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="whenDate"
              :label="whenHeadline"
              name="bike-theft-claim-flow-when"
            >
              <div class="row">
                <div class="columns medium-6">
                  <BaseMomentDatePicker
                    v-bind="field"
                    :min-date="whenDateMinimumSelectionDate"
                    :max-date="whenDateMaximumSelectionDate"
                    :model-value="whenDate"
                  />
                </div>
              </div>
              <ValidationError name="bike-theft-claim-flow-when" />
            </Field>
          </div>
        </article>

        <!-- Beskrivelse -->
        <article class="flow-group">
          <h3 v-html="descriptionHeadline" />
          <p v-html="descriptionText" />
          <div class="control">
            <base-text-area
              v-model="description"
              :label="descriptionHeadline"
              name="bike-theft-claim-flow-description"
            />
          </div>
        </article>

        <!-- Hvem ejer cyklen -->
        <article class="flow-group">
          <h3 v-html="whoOwnsTheBikeHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="whoOwnsTheBike"
              :rules="'required'"
              :label="whoOwnsTheBikeHeadline"
              name="bike-theft-claim-flow-who-owns-the-bike"
            >
              <base-radio-buttons-stacked
                v-bind="field"
                :options="whoOwnsTheBikeOptions"
                :model-value="whoOwnsTheBike"
              />
              <ValidationError name="bike-theft-claim-flow-who-owns-the-bike" />
            </Field>

            <div v-if="showOtherOwnerNameField" class="row">
              <div class="columns medium-6">
                <p v-html="otherOwnerNameText" />
                <Field
                  v-slot="{ field }"
                  v-model="whoOwnsTheBikeOtherName"
                  :rules="{ required: showOtherOwnerNameField }"
                  :label="otherOwnerNameText"
                  name="bike-theft-claim-flow-who-owns-the-bike-other-name"
                >
                  <base-text-input v-bind="field" />
                  <ValidationError name="bike-theft-claim-flow-who-owns-the-bike-other-name" />
                </Field>
              </div>
            </div>
          </div>
        </article>

        <!-- Hvad er der sket -->
        <article class="flow-group">
          <h3 v-html="whatHappenedHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="whatHappened"
              :rules="'required'"
              :label="whatHappenedHeadline"
              name="bike-theft-claim-flow-what-happened"
            >
              <base-radio-buttons-stacked
                v-bind="field"
                :options="whatHappenedOptions"
                :model-value="whatHappened"
              />
              <ValidationError name="bike-theft-claim-flow-what-happened" />
            </Field>
          </div>
        </article>

        <!-- Har du cyklens stelnummer -->
        <article class="flow-group">
          <h3 v-html="memberHasBikeFrameNumberHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="memberHasBikeFrameNumber"
              :rules="'required'"
              :label="memberHasBikeFrameNumberHeadline"
              name="bike-theft-claim-flow-member-has-bike-frame-number"
            >
              <base-radio-button
                v-bind="field"
                id="bike-theft-claim-flow-member-has-bike-frame-number-yes"
                :option="memberHasBikeFrameNumberOptions[0]"
                :model-value="memberHasBikeFrameNumber"
              />

              <div v-if="showBikeFrameNumberField" class="row">
                <div class="columns medium-6">
                  <p v-html="memberHasBikeFrameNumberText" />
                  <Field
                    v-slot="{ field }"
                    v-model="bikeFrameNumber"
                    :rules="{ required: showBikeFrameNumberField }"
                    :label="memberHasBikeFrameNumberText"
                    name="bike-theft-claim-flow-bike-frame-number"
                  >
                    <base-text-input v-bind="field" />
                  </Field>
                  <ValidationError name="bike-theft-claim-flow-bike-frame-number" />
                </div>
              </div>

              <base-radio-button
                v-bind="field"
                id="bike-theft-claim-flow-member-has-bike-frame-number-no"
                :option="memberHasBikeFrameNumberOptions[1]"
                :model-value="memberHasBikeFrameNumber"
              />
              <ValidationError name="bike-theft-claim-flow-member-has-bike-frame-number" />
            </Field>
            <info-box :arrow-show="false" :text="memberHasBikeFrameNumberInfoBox" />
          </div>
        </article>

        <!-- Var cyklen låst -->
        <article v-if="showWasTheBikeLocked" class="flow-group">
          <h3 v-html="wasTheBikeLockedHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="wasTheBikeLocked"
              :rules="'required'"
              :label="wasTheBikeLockedHeadline"
              name="bike-theft-claim-flow-was-the-bike-locked"
            >
              <base-radio-buttons-stacked
                v-bind="field"
                :options="wasTheBikeLockedOptions"
                :model-value="wasTheBikeLocked"
              />
              <ValidationError name="bike-theft-claim-flow-was-the-bike-locked" />
            </Field>
          </div>
        </article>

        <!-- Hvordan var cyklen låst -->
        <article v-if="showHowWasTheBikeLocked" class="flow-group">
          <h3 v-html="howWasTheBikeLockedHeadline" />
          <div class="control">
            <Field
              v-slot="{ field }"
              v-model="howWasTheBikeLocked"
              :rules="'required'"
              :label="howWasTheBikeLockedHeadline"
              name="bike-theft-claim-flow-how-was-the-bike-locked"
            >
              <base-radio-buttons-stacked
                v-bind="field"
                :options="howWasTheBikeLockedOptions"
                :model-value="howWasTheBikeLocked"
              />
              <ValidationError name="bike-theft-claim-flow-how-was-the-bike-locked" />
            </Field>

            <div v-if="showDescribeHowTheBikeWasLockedField">
              <p v-html="describeHowTheBikeWasLockedText" />
              <Field
                v-slot="{ field }"
                v-model="describeHowTheBikeWasLocked"
                :rules="{ required: showDescribeHowTheBikeWasLockedField }"
                :label="describeHowTheBikeWasLockedText"
                name="bike-theft-claim-flow-describe-how-the-bike-was-locked"
              >
                <base-text-area v-bind="field" />
                <ValidationError name="bike-theft-claim-flow-describe-how-the-bike-was-locked" />
              </Field>
            </div>

            <info-box :arrow-show="false" :text="howWasTheBikeLockedInfoBox" />
          </div>
        </article>

        <div class="row">
          <div class="columns medium-6">
            <router-link
              class="button secondary text-center"
              to="/opret-cykel-skade"
              data-test-bike-theft-claim-flow-link-button-back
            >
              Tilbage
            </router-link>
          </div>
          <div class="columns medium-6">
            <router-link
              class="button text-center"
              to="/opret-cykel-skade/what/bike"
              data-test-bike-theft-claim-flow-link-button-next
            >
              Videre
            </router-link>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import BaseTextInput from '@/components/Forms/BaseTextInput.vue';
import BaseMomentDatePicker from '@/components/Forms/BaseMomentDatePicker.vue';
import BaseRadioButton from '@/components/Forms/BaseRadioButton.vue';
import BaseRadioButtonsStacked from '@/components/Forms/BaseRadioButtonsStacked.vue';
import BaseTextArea from '@/components/Forms/BaseTextArea.vue';
import BeforeRouteLeaveMixin from '../mixins/beforeRouteLeave';
import InfoBox from '@/components/UI/InfoBox.vue';
import ValidationError from '@/components/UI/ValidationError.vue';

export default {
  name: 'WhatHappened',
  components: {
    BaseMomentDatePicker,
    BaseRadioButton,
    BaseRadioButtonsStacked,
    BaseTextArea,
    BaseTextInput,
    ValidationError,
    InfoBox
  },
  mixins: [BeforeRouteLeaveMixin],
  beforeRouteLeave(to, from, next) {
    this.leaveRoute(to, from, next);
  },
  computed: {
    ...mapState({
      describeHowTheBikeWasLockedText: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.describeHowTheBikeWasLockedText,
      description: (state) => state.bikeTheftClaimFlow.data.descriptionPage.description,
      descriptionHeadline: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.descriptionHeadline,
      descriptionText: (state) => state.bikeTheftClaimFlow.data.descriptionPage.descriptionText,
      memberHasBikeFrameNumberHeadline: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.memberHasBikeFrameNumberHeadline,
      memberHasBikeFrameNumberInfoBox: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.memberHasBikeFrameNumberInfoBox,
      memberHasBikeFrameNumberOptions: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.memberHasBikeFrameNumberOptions,
      memberHasBikeFrameNumberText: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.memberHasBikeFrameNumberText,
      otherOwnerNameText: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.otherOwnerNameText,
      howWasTheBikeLockedHeadline: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.howWasTheBikeLockedHeadline,
      howWasTheBikeLockedInfoBox: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.howWasTheBikeLockedInfoBox,
      howWasTheBikeLockedOptions: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.howWasTheBikeLockedOptions,
      wasTheBikeLockedHeadline: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.wasTheBikeLockedHeadline,
      wasTheBikeLockedOptions: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.wasTheBikeLockedOptions,
      whenDateMaximumSelectionDate: (state) =>
        moment(state.bikeTheftClaimFlow.data.descriptionPage.whenDateMaximumSelectionDate),
      whenDateMinimumSelectionDate: (state) =>
        moment(state.bikeTheftClaimFlow.data.descriptionPage.whenDateMinimumSelectionDate),
      whoOwnsTheBikeHeadline: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.whoOwnsTheBikeHeadline,
      whoOwnsTheBikeOptions: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.whoOwnsTheBikeOptions,
      stepHeadline: (state) => state.bikeTheftClaimFlow.data.descriptionPage.stepHeadline,
      whatHappenedHeadline: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.whatHappenedHeadline,
      whatHappenedOptions: (state) =>
        state.bikeTheftClaimFlow.data.descriptionPage.whatHappenedOptions,
      whenHeadline: (state) => state.bikeTheftClaimFlow.data.descriptionPage.whenHeadline
    }),
    bikeFrameNumber: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.bikeFrameNumber;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setBikeFrameNumber', value);
      }
    },
    describeHowTheBikeWasLocked: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.describeHowTheBikeWasLocked;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setDescribeHowTheBikeWasLocked', value);
      }
    },
    description: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.description;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setDescription', value);
      }
    },
    memberHasBikeFrameNumber: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.memberHasBikeFrameNumberSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setMemberHasBikeFrameNumber', value);
      }
    },
    howWasTheBikeLocked: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.howWasTheBikeLockedSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setHowWasTheBikeLocked', value);
      }
    },
    wasTheBikeLocked: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.wasTheBikeLockedSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setWasTheBikeLocked', value);
      }
    },
    whatHappened: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.whatHappenedSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setWhatHappened', value);
      }
    },
    whenDate: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.whenDate;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setWhenDate', value);
      }
    },
    whoOwnsTheBike: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.whoOwnsTheBikeSelectedValue;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setWhoOwnsTheBike', value);
      }
    },
    whoOwnsTheBikeOtherName: {
      get() {
        return this.$store.state.bikeTheftClaimFlow.data.submitData
          .bicycleClaimQuestionsTemplateModel.whoOwnsTheBikeOtherName;
      },
      set(value) {
        this.$store.commit('bikeTheftClaimFlow/setWhoOwnsTheBikeOtherName', value);
      }
    },
    showBikeFrameNumberField() {
      return this.memberHasBikeFrameNumber === 'Ja';
    },
    showDescribeHowTheBikeWasLockedField() {
      return this.howWasTheBikeLocked === 'Cyklen var l&#229;st p&#229; en anden m&#229;de';
    },
    showHowWasTheBikeLocked() {
      return this.showWasTheBikeLocked && this.wasTheBikeLocked === 'Ja';
    },
    showOtherOwnerNameField() {
      return this.whoOwnsTheBike === 'Andre';
    },
    showWasTheBikeLocked() {
      return this.whatHappened === 'Tyveri af cykel';
    }
  },
  watch: {
    howWasTheBikeLocked() {
      this.$store.dispatch('bikeTheftClaimFlow/setDescribeHowTheBikeWasLocked', null);
    },
    memberHasBikeFrameNumber() {
      this.$store.dispatch('bikeTheftClaimFlow/setBikeFrameNumber', null);
    },
    showOtherOwnerNameField() {
      this.$store.dispatch('bikeTheftClaimFlow/setWhoOwnsTheBikeOtherName', null);
    },
    wasTheBikeLocked() {
      this.$store.dispatch('bikeTheftClaimFlow/setHowWasTheBikeLocked', null);
    },
    whatHappened() {
      this.$store.dispatch('bikeTheftClaimFlow/setWasTheBikeLocked', null);
    }
  }
};
</script>
